import React, { useState, useRef, useEffect, useContext } from 'react';
import { Box, Paper, Typography, IconButton, Button, Chip, Divider, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close as CloseIcon, PlayArrow as PlayIcon, Pause as PauseIcon, Stop as StopIcon, Timer as TimerIcon, Schedule as ScheduleIcon, Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import AnalogClock from './AnalogClock';
import { ThemeContext } from '../App';
import { debug } from '../utils/debug';
import { ensureComponentInViewport } from '../utils/detachableHelpers';

const DetachableClock = ({ onClose }) => {
  const {
    mode,
    weatherCondition,
    weatherThemeEnabled,
    weatherThemeMode,
    selectedWeatherTheme,
    solidDetachableComponents
  } = useContext(ThemeContext);

  const isDarkMode = mode === 'dark';
  const theme = useTheme();
  
  // Position on the right side by default, with localStorage support
  const [position, setPosition] = useState(() => {
    // Get position and ensure it's within viewport
    return ensureComponentInViewport(
      'expenseClockPosition',
      { x: window.innerWidth - 280, y: 80 },
      280,
      350 // Compact height for timer functionality
    );
  });
  
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [currentTime, setCurrentTime] = useState(new Date());
  const clockRef = useRef(null);

  // Timer state
  const [timerMinutes, setTimerMinutes] = useState(60); // Default to 60 minutes
  const [timerSeconds, setTimerSeconds] = useState(60 * 60); // Initialize with 60 minutes in seconds
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerStartTime, setTimerStartTime] = useState(null);
  const [showTimer, setShowTimer] = useState(false);
  const timerRef = useRef(null);

  // Quick timer options
  const quickOptions = [10, 15, 30, 45, 60];

  // Update digital time display
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 10000); // Update every 10 seconds
    
    return () => clearInterval(timer);
  }, []);

  // Timer logic. The tick itself only ever needs isTimerRunning (whether to
  // be running at all) and reads/writes timerSeconds through the functional
  // setTimerSeconds(prev => ...) updater, so it doesn't need timerSeconds'
  // current value at all - timerSeconds was still in this effect's
  // dependency array, though, which tore the interval down and recreated it
  // every single second the timer ran (since setTimerSeconds's tick
  // changes timerSeconds every second) instead of once per start/pause.
  useEffect(() => {
    if (isTimerRunning && timerSeconds > 0) {
      timerRef.current = setInterval(() => {
        setTimerSeconds(prev => {
          if (prev <= 1) {
            // Timer finished
            setIsTimerRunning(false);
            setTimerStartTime(null);
            // Show notification or alert
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('Timer Finished!', {
                body: `Your ${timerMinutes} minute timer has completed.`,
                icon: '/favicon.ico'
              });
            } else {
              alert(`Timer finished! Your ${timerMinutes} minute timer has completed.`);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimerRunning, timerMinutes]);

  // Request notification permission on component mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);
  
  // Add window resize handler to keep component in viewport
  useEffect(() => {
    const handleResize = () => {
      // Only update position if not currently dragging
      if (!isDragging) {
        const newPosition = ensureComponentInViewport(
          'expenseClockPosition',
          { x: window.innerWidth - 280, y: 80 },
          280,
          350, // Updated height
          true // Maintain right distance
        );
        setPosition(newPosition);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isDragging]);

  // Timer functions
  const startTimer = () => {
    setIsTimerRunning(true);
    setTimerStartTime(Date.now());
  };

  const pauseTimer = () => {
    setIsTimerRunning(false);
    setTimerStartTime(null);
  };

  const stopTimer = () => {
    setIsTimerRunning(false);
    setTimerSeconds(timerMinutes * 60);
    setTimerStartTime(null);
  };

  const setQuickTimer = (minutes) => {
    setTimerMinutes(minutes);
    setTimerSeconds(minutes * 60);
    setIsTimerRunning(false);
    setTimerStartTime(null);
  };

  const adjustTimer = (increment) => {
    if (!isTimerRunning) {
      const newMinutes = Math.max(1, Math.min(999, timerMinutes + increment));
      setTimerMinutes(newMinutes);
      setTimerSeconds(newMinutes * 60);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerProgress = () => {
    const totalSeconds = timerMinutes * 60;
    if (totalSeconds === 0) return 0;
    return ((totalSeconds - timerSeconds) / totalSeconds) * 100;
  };

  // Handle mouse down event to start dragging
  const handleMouseDown = (e) => {
    if (e.target.closest('.close-btn')) return; // Don't start drag when clicking close button
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  // Handle mouse move event when dragging.
  // Coalesced into at most one setPosition (and therefore one re-render)
  // per animation frame via requestAnimationFrame, instead of one per
  // mousemove event - mousemove can fire at a much higher rate than the
  // display can even show (well over 60/s on many mice/trackpads), so this
  // was re-rendering the whole widget far more often than any of it was
  // ever visible for.
  const dragPositionRef = useRef(null);
  const dragRafRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    dragPositionRef.current = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    };

    if (dragRafRef.current === null) {
      dragRafRef.current = requestAnimationFrame(() => {
        dragRafRef.current = null;
        setPosition(dragPositionRef.current);
      });
    }
  };

  // Handle mouse up event to stop dragging
  const handleMouseUp = (e) => {
    // Cancel any still-pending throttled move update - it holds a stale
    // position that would otherwise land after (and overwrite) the final
    // adjusted one set below.
    if (dragRafRef.current !== null) {
      cancelAnimationFrame(dragRafRef.current);
      dragRafRef.current = null;
    }

    if (isDragging) {
      // Calculate raw position first
      const rawPosition = {
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      };

      // Ensure the position is within the viewport bounds
      const adjustedPosition = ensureComponentInViewport(
        'expenseClockPosition',
        rawPosition,
        280,
        350
      );

      // Update state with the adjusted position
      setPosition(adjustedPosition);
    }
    setIsDragging(false);
  };

  // Add/remove event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (dragRafRef.current !== null) {
        cancelAnimationFrame(dragRafRef.current);
        dragRafRef.current = null;
      }
    };
  }, [isDragging, dragStart, position]);

  // Save position to localStorage when component unmounts
  useEffect(() => {
    return () => {
      localStorage.setItem('expenseClockPosition', JSON.stringify(position));
    };
  }, [position]);

  // Get the effective weather condition based on theme mode
  const effectiveWeatherCondition = weatherThemeEnabled
    ? (weatherThemeMode === 'manual' ? selectedWeatherTheme : weatherCondition)
    : null;
    
  debug('clock', 'Weather condition', effectiveWeatherCondition);
  debug('clock', 'Weather theme enabled', weatherThemeEnabled);

  // Dynamic background color with weather theme support
  const pageBackgroundColor = theme.palette?.background?.default || (isDarkMode ? '#121212' : '#f5f5f5');

  // Dynamic background color
  const bgColor = pageBackgroundColor;
      
  debug('clock', 'Background color', bgColor);

  // Get accent color based on weather condition
  const getAccentColor = (condition, isDark) => {
    condition = condition.toLowerCase();
    
    switch(condition) {
      case 'clear':
        return isDark ? '#ffa000' : '#ff9800'; // Amber for clear/sunny
      case 'clouds':
        return isDark ? '#78909c' : '#607d8b'; // Blue-gray for clouds
      case 'rain':
      case 'drizzle':
        return isDark ? '#26c6da' : '#00acc1'; // Cyan/teal for rain
      case 'snow':
        return isDark ? '#90caf9' : '#42a5f5'; // Blue for snow
      case 'thunderstorm':
        return isDark ? '#9575cd' : '#5e35b1'; // Purple for thunderstorm
      case 'spring':
        return isDark ? '#66bb6a' : '#81c784'; // Green for spring
      case 'summer':
        return isDark ? '#26c6da' : '#00bcd4'; // Cyan for summer
      case 'autumn':
        return isDark ? '#ff7043' : '#ff5722'; // Deep orange for autumn
      case 'winter':
        return isDark ? '#90caf9' : '#2196f3'; // Blue for winter
      default:
        return isDark ? '#bb86fc' : '#1976d2';
    }
  };
  
  const accentColor = weatherThemeEnabled && effectiveWeatherCondition
    ? getAccentColor(effectiveWeatherCondition, isDarkMode)
    : (isDarkMode ? '#bb86fc' : '#1976d2');

  // Get clock face background color (same as AnalogClock component)
  const getWeatherClockFace = (condition, isDark) => {
    const defaultBgColor = isDark ? '#1e1e1e' : 'white';
    if (!condition) return defaultBgColor;
    condition = condition.toLowerCase();
    
    switch(condition) {
      case 'clear':
        return isDark ? '#ff9100' : '#ffe082';  // Amber for clear/sunny
      case 'clouds':
        return isDark ? '#263238' : '#cfd8dc';  // Gray for cloudy
      case 'rain':
      case 'drizzle':
        return isDark ? '#00695c' : '#e0f7fa';  // Teal/cyan for rainy
      case 'snow':
        return isDark ? '#546e7a' : '#e1f5fe';  // Pale blue for snowy
      case 'thunderstorm':
        return isDark ? '#4a148c' : '#d1c4e9';  // Purple for stormy
      case 'spring':
        return isDark ? '#43a047' : '#c8e6c9';  // Green for spring
      case 'summer':
        return isDark ? '#0097a7' : '#b2ebf2';  // Cyan for summer
      case 'autumn':
        return isDark ? '#e64a19' : '#ffccbc';  // Orange for autumn
      case 'winter':
        return isDark ? '#1976d2' : '#e3f2fd';  // Blue for winter
      default:
        return defaultBgColor;
    }
  };

  // Clock hands color (same logic as AnalogClock component)
  const handColor = (weatherThemeEnabled && effectiveWeatherCondition) ? accentColor : textColor;

  // Weather icon to display as a decorative element
  const getWeatherIcon = () => {
    if (!effectiveWeatherCondition) return '';
    
    switch(effectiveWeatherCondition.toLowerCase()) {
      case 'clear': return '☀️';
      case 'clouds': return '☁️';
      case 'rain': case 'drizzle': return '🌧️';
      case 'snow': return '❄️';
      case 'thunderstorm': return '⚡';
      case 'spring': return '🌱';
      case 'summer': return '🏖️';
      case 'autumn': return '🍂';
      case 'winter': return '☃️';
      default: return '';
    }
  };

  // Dynamic text colors sourced from the palette so the widget mirrors the dashboard surface
  const textColor = theme.palette?.text?.primary || (isDarkMode ? '#e0e0e0' : '#212529');

  const secondaryTextColor = theme.palette?.text?.secondary || (isDarkMode ? '#a0a0a0' : '#6c757d');

  const borderColor = theme.palette?.divider || (isDarkMode ? '#333333' : '#dee2e6');

  return (
    <Paper
      ref={clockRef}
      elevation={0}
      sx={{
        position: 'fixed',
        top: `${position.y}px`,
        left: `${position.x}px`,
        zIndex: 1050,
        padding: '12px',
        paddingBottom: '20px',
        borderRadius: '12px',
        boxShadow: 'none',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        width: '280px',
        height: '350px', // Compact height for timer functionality
        transition: 'background 0.5s ease, transform 0.2s ease, color 0.3s ease',
        background: bgColor,
        border: `1px solid ${borderColor}`,
        color: textColor,
        '&:hover': {
          transform: 'translateY(-2px)'
        }
      }}
      onMouseDown={handleMouseDown}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ 
            m: 0, 
            color: textColor,
            display: 'flex',
            alignItems: 'center'
          }}>
            {weatherThemeEnabled && effectiveWeatherCondition && getWeatherIcon()}
            <span style={{ marginLeft: weatherThemeEnabled && effectiveWeatherCondition ? '4px' : 0 }}>
              Clock
            </span>
          </Typography>
          <Tooltip title={showTimer ? "Show Clock" : "Show Timer"}>
            <IconButton
              size="small"
              onClick={() => setShowTimer(!showTimer)}
              sx={{
                color: secondaryTextColor,
                '&:hover': { color: textColor }
              }}
            >
              {showTimer ? <ScheduleIcon fontSize="small" /> : <TimerIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          
          {/* Compact Timer Display */}
          {!showTimer && isTimerRunning && (
            <Box 
              onClick={() => setShowTimer(true)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: getWeatherClockFace(effectiveWeatherCondition, isDarkMode),
                border: `1px solid ${textColor}`,
                boxShadow: weatherThemeEnabled && effectiveWeatherCondition 
                  ? `0 0 8px ${accentColor}30, inset 0 0 3px rgba(0, 0, 0, 0.1)` 
                  : 'inset 0 0 3px rgba(0, 0, 0, 0.1)',
                transition: 'background-color 0.5s ease, color 0.3s ease, box-shadow 0.3s ease, transform 0.2s ease',
                ml: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: weatherThemeEnabled && effectiveWeatherCondition 
                    ? `0 0 12px ${accentColor}40, inset 0 0 5px rgba(0, 0, 0, 0.15)` 
                    : 'inset 0 0 5px rgba(0, 0, 0, 0.15)'
                }
              }}
            >
              <Typography 
                variant="caption" 
                sx={{ 
                  fontFamily: '"Roboto Mono", "SF Mono", Monaco, monospace',
                  fontWeight: 600,
                  color: handColor,
                  fontSize: '0.75rem',
                  letterSpacing: '0.5px',
                  textShadow: isTimerRunning ? `0 0 6px ${handColor}50` : 'none'
                }}
              >
                {formatTime(timerSeconds)}
              </Typography>
            </Box>
          )}
        </Box>
        <IconButton 
          size="small" 
          className="close-btn"
          onClick={onClose}
          sx={{ 
            position: 'absolute',
            top: '10px',
            right: '10px',
            fontSize: '1.1rem',
            color: secondaryTextColor, 
            opacity: 0.6,
            transition: 'all 0.2s ease',
            '&:hover': { 
              color: textColor,
              opacity: 1,
              transform: 'scale(1.1)'
            }
          }}
          aria-label="Close clock"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      
      {!showTimer ? (
        // Clock View
        <Box sx={{ textAlign: 'center' }}>
          <AnalogClock size={190} />
          <Typography 
            variant="h5" 
            sx={{ 
              mt: 2, 
              mb: 1, 
              fontWeight: 'bold', 
              fontFamily: 'monospace',
              letterSpacing: '0.5px',
              lineHeight: '1.2',
              color: textColor
            }}
          >
            {currentTime.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 0,
              fontSize: '0.85rem',
              opacity: '0.9',
              lineHeight: '1.2',
              color: secondaryTextColor
            }}
          >
            {currentTime.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
        </Box>
      ) : (
        // Timer View
        <Box sx={{ textAlign: 'center', px: 0.5 }}>
          {/* Timer Display */}
          <Box sx={{ 
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            background: isDarkMode 
              ? 'linear-gradient(145deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))'
              : 'linear-gradient(145deg, rgba(0,0,0,0.02), rgba(0,0,0,0.05))',
            border: `1px solid ${borderColor}`,
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Subtle background pattern */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.03,
                background: `radial-gradient(circle at 50% 50%, ${accentColor} 1px, transparent 1px)`,
                backgroundSize: '15px 15px',
                pointerEvents: 'none'
              }}
            />
            
            {/* Timer adjustment controls */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: 2,
              mb: 1
            }}>
              <IconButton
                size="small"
                onClick={() => adjustTimer(-1)}
                disabled={isTimerRunning || timerMinutes <= 1}
                sx={{
                  color: accentColor,
                  backgroundColor: `${accentColor}15`,
                  border: `1px solid ${accentColor}40`,
                  '&:hover': {
                    backgroundColor: `${accentColor}25`,
                    transform: 'scale(1.1)'
                  },
                  '&:disabled': {
                    opacity: 0.3
                  }
                }}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              
              <Typography 
                variant="body2" 
                sx={{ 
                  color: secondaryTextColor,
                  fontWeight: 500,
                  minWidth: 60,
                  textAlign: 'center'
                }}
              >
                {timerMinutes} min
              </Typography>
              
              <IconButton
                size="small"
                onClick={() => adjustTimer(1)}
                disabled={isTimerRunning || timerMinutes >= 999}
                sx={{
                  color: accentColor,
                  backgroundColor: `${accentColor}15`,
                  border: `1px solid ${accentColor}40`,
                  '&:hover': {
                    backgroundColor: `${accentColor}25`,
                    transform: 'scale(1.1)'
                  },
                  '&:disabled': {
                    opacity: 0.3
                  }
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
            
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 300, 
                fontFamily: '"Roboto Mono", "SF Mono", Monaco, monospace',
                letterSpacing: '3px',
                color: isTimerRunning ? accentColor : textColor,
                textShadow: isTimerRunning ? `0 0 15px ${accentColor}40` : 'none',
                transition: 'all 0.3s ease',
                position: 'relative',
                zIndex: 1
              }}
            >
              {formatTime(timerSeconds)}
            </Typography>
          </Box>

          {/* Timer Controls */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Button
                variant={isTimerRunning ? "outlined" : "contained"}
                size="small"
                onClick={isTimerRunning ? pauseTimer : startTimer}
                disabled={timerSeconds === 0}
                startIcon={isTimerRunning ? <PauseIcon /> : <PlayIcon />}
                sx={{ 
                  minWidth: 85,
                  height: 36,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  color: isTimerRunning ? accentColor : 'white',
                  backgroundColor: isTimerRunning ? 'transparent' : accentColor,
                  borderColor: accentColor,
                  borderWidth: 1.5,
                  boxShadow: isTimerRunning ? 'none' : `0 3px 8px ${accentColor}40`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: isTimerRunning ? `${accentColor}15` : `${accentColor}dd`,
                    transform: 'translateY(-1px)',
                    boxShadow: isTimerRunning ? `0 2px 6px ${accentColor}30` : `0 4px 12px ${accentColor}50`
                  },
                  '&:active': {
                    transform: 'translateY(0px)'
                  }
                }}
              >
                {isTimerRunning ? 'Pause' : 'Start'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={stopTimer}
                disabled={timerSeconds === 0 && !isTimerRunning}
                startIcon={<StopIcon />}
                sx={{ 
                  minWidth: 85,
                  height: 36,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  color: secondaryTextColor,
                  borderColor: borderColor,
                  borderWidth: 1.5,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: textColor,
                    color: textColor,
                    backgroundColor: `${textColor}08`,
                    transform: 'translateY(-1px)'
                  },
                  '&:active': {
                    transform: 'translateY(0px)'
                  }
                }}
              >
                Stop
              </Button>
            </Box>
          </Box>

          <Divider sx={{ 
            my: 2, 
            borderColor: borderColor,
            '&::before, &::after': {
              borderColor: borderColor
            }
          }} />

          {/* Quick Timer Options */}
          <Box sx={{ mb: 2 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1.5, 
                color: secondaryTextColor,
                fontWeight: 500,
                letterSpacing: '0.3px',
                fontSize: '0.8rem'
              }}
            >
              Quick Timer
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 0.75, 
              justifyContent: 'center',
              px: 0.5
            }}>
              {quickOptions.map((minutes) => (
                <Chip
                  key={minutes}
                  label={`${minutes}m`}
                  size="small"
                  variant={timerMinutes === minutes ? "filled" : "outlined"}
                  onClick={() => setQuickTimer(minutes)}
                  sx={{
                    minWidth: 38,
                    height: 28,
                    borderRadius: 1.5,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: timerMinutes === minutes ? 'white' : textColor,
                    backgroundColor: timerMinutes === minutes ? accentColor : 'transparent',
                    borderColor: timerMinutes === minutes ? accentColor : borderColor,
                    borderWidth: 1.5,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: timerMinutes === minutes ? accentColor : `${accentColor}20`,
                      borderColor: accentColor,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 3px 6px ${accentColor}30`
                    },
                    '&:active': {
                      transform: 'translateY(0px)'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>


        </Box>
      )}
    </Paper>
  );
};

export default DetachableClock; 